// @flow

// AgGridChart
//
// A single chart representation using Ag-grid's built in chart api.
// > The charts package is currently in Beta
// https://www.ag-grid.com/javascript-grid-charts-customisation-bar/
//
// The chart implementation is specifically geared towards spreadsheet style
// charts driven by grid data.  For that reason the to drive these charts an
// instance of Ag-grid is attached to the DOM but hidden; by showGrid.
//
// It's a readonly grid, if you're looking for a user input grid for editing
// records you should be looking at the SchemaTable.
//
// This component should be driven by configuration, if you find yourself making
// changes here stop and think... should it be a different abstraction e.g.
// MyCustomGrid.

import React from "react";
import {
  AgGridReact,
  type CellRangeParams,
  BarChartOptions,
  LineChartOptions,
  ScatterChartOptions,
  AreaChartOptions,
  PieChartOptions,
  DoughnutChartOptions,
  ChartRef,
} from "ag-grid-react";
import "ag-grid-enterprise/chartsModule";
import { chartDefaultColors } from "@styles/colors";

export type GridChartProps = {
  width: number | string,
  height: number | string,
  columnDefs: *,
  rowData: *,
  options:
    | BarChartOptions
    | LineChartOptions
    | ScatterChartOptions
    | AreaChartOptions
    | PieChartOptions
    | DoughnutChartOptions,
  chartRangeParams: CellRangeParams,
  colors: Array<string>,
  showGrid: ?boolean,
  id: string,
  title: string,
  titleFontSize?: number,
};

type State = $Exact<{
  forceRefresh: boolean,
}>;

export class AgGridChart extends React.Component<GridChartProps, State> {
  static defaultProps = {
    width: "100%",
    height: "400px",
    colors: chartDefaultColors,
    showGrid: false,
    id: "",
    title: "",
    options: {},
  };

  state = {
    forceRefresh: false,
  };

  chartRef: ChartRef;
  currentChartRef: ChartRef;

  componentDidUpdate(prevProps: GridChartProps) {
    if (
      this.props.rowData &&
      JSON.stringify(this.props.rowData) !== JSON.stringify(prevProps.rowData)
    ) {
      // Why do we need to do a manual hard reload?
      // here is an intermitten issue with chart updates where the data / data series are lost when updating between different report data.
      // There's doesn't appear to be any reason for this (except maybe timing) but the charts are still in Beta; in the future this may not be needed.
      this.setState({ forceRefresh: true }, () => {
        setTimeout(() => this.setState({ forceRefresh: false }), 0);
      });
    }
  }

  onFirstDataRendered = (params: *) => {
    const chartRangeParams = {
      ...this.props.chartRangeParams,
      chartContainer: document.querySelector(`#myChart_${this.props.id}`),
    };

    this.chartRef = params.api.chartRange(chartRangeParams);
  };

  createChartContainer = (chartRef: ChartRef) => {
    if (this.currentChartRef) {
      this.currentChartRef.destroyChart();
    }

    const chartEl = chartRef.chartElement;
    const parentEl = document.querySelector(`#myChart_${this.props.id}`);
    parentEl && parentEl.appendChild(chartEl);
    this.currentChartRef = chartRef;
  };

  processChartOptions = (params: *) => ({
    ...params.options,
    ...(this.props.options || {}),
    title: {
      text: this.props.title,
      fontStyle: "normal",
      fontWeight: "bolder",
      fontSize: this.props.titleFontSize || 20,
      fontFamily: "SharpGroteskBook19, Arial, Helvetica Neue, sans-serif",
      color: "black",
      ...(this.props.options.title || {}),
    },
    legendPosition: this.props.options.legendPosition || "top",
    legend: {
      ...params.options.legend,
      labelRotation: 0,
      labelFontSize: 14,
      fontFamily: "SharpGroteskBook19, Arial, Helvetica Neue, sans-serif",
      ...(this.props.options.legend || {}),
    },
    seriesDefaults: {
      ...params.options.seriesDefaults,
      fills: this.props.colors,
      strokes: this.props.colors,
      fillOpacity: 1,
      strokeOpacity: 1,
      highlightStyle: {
        fill: "#6B4E45",
        stroke: "#6B4E45",
      },
      labelEnabled:
        this.props.options.labelEnabled !== "undefined"
          ? this.props.options.labelEnabled
          : params.type === "pie" || params.type === "doughnut",
      labelMinAngle: 3,
      labelFontStyle: "italic",
      labelFontWeight: "normal",
      labelFontSize: 14,
      fontFamily: "SharpGroteskBook19, Arial, Helvetica Neue, sans-serif",
      strokeWidth: 3,
      calloutStrokeWidth: 3,
      calloutColors: ["#555"],
      calloutLength: 15,
      calloutPadding: 15,
      ...(this.props.options.seriesDefaults || {}),
    },
    xAxis: {
      ...params.options.xAxis,
      labelRotation: 0,
      labelFontSize: 14,
      fontFamily: "SharpGroteskBook19, Arial, Helvetica Neue, sans-serif",
      ...(this.props.options.xAxis || {}),
    },
    yAxis: {
      ...params.options.yAxis,
      labelRotation: 0,
      labelFontSize: 14,
      fontFamily: "SharpGroteskBook19, Arial, Helvetica Neue, sans-serif",
      ...(this.props.options.yAxis || {}),
    },
  });

  render() {
    if (this.state.forceRefresh) {
      return <div />;
    }

    const { width, height, showGrid, id } = this.props;

    return (
      <div className="ag-theme-balham" style={{ width: "100%" }}>
        <div
          style={
            showGrid
              ? {
                  height: typeof height === "number" ? `${height}px` : height,
                  width: typeof width === "number" ? `${width}px` : width,
                }
              : {
                  height: "0",
                  width: "100%",
                }
          }
        >
          <AgGridReact
            columnDefs={this.props.columnDefs}
            rowData={this.props.rowData}
            animateRows={true}
            enableCharts={true}
            enableRangeSelection={true}
            processChartOptions={this.processChartOptions}
            createChartContainer={this.createChartContainer}
            onFirstDataRendered={this.onFirstDataRendered}
            {...this.props}
          />
        </div>
        <div
          id={`myChart_${id}`}
          className="my-chart"
          style={{
            height: typeof height === "number" ? `${height}px` : height,
            width: typeof width === "number" ? `${width}px` : width,
          }}
        />
      </div>
    );
  }
}

export default AgGridChart;
